import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { dt as fmtDate } from "@/lib/format";
import { Button, Input, Modal, PageHeader, Pill, RowActions, Table, Td, Th } from "@/components/ui";
import { popup } from "@/components/popup";

interface TicketMsg {
  id: number;
  author: string;
  text: string | null;
  has_attachment: boolean;
  created_at: string | null;
}

interface Ticket {
  id: number;
  user_id: number;
  user_telegram_id: number | null;
  username: string | null;
  status: string;
  agent_id: number | null;
  agent_name: string | null;
  rating: number | null;
  form: Record<string, string | null> | null;
  created_at: string | null;
  updated_at: string | null;
}

interface TicketDetail extends Ticket {
  messages: TicketMsg[];
}

const STATUS: Record<string, { label: string; tone: "neutral" | "success" | "warning" | "danger" }> = {
  new: { label: "новое", tone: "danger" },
  in_progress: { label: "в работе", tone: "success" },
  waiting_user: { label: "ждём юзера", tone: "warning" },
  closed: { label: "закрыто", tone: "neutral" },
};

/** Оценка: одна звезда + число, а не пять иконок подряд — в узкой колонке
 *  «★★★★★» не читается. Клампим: у тикетов эпохи «решено/не решено» rating
 *  бывает −1, и "⭐".repeat(-1) роняет весь экран RangeError'ом. */
function stars(rating: number | null) {
  if (!rating || rating < 1) return <span className="text-zinc-600">—</span>;
  return (
    <span className="whitespace-nowrap tabular-nums">
      ⭐ {Math.min(5, rating)}
    </span>
  );
}

const FIELDS: Record<string, string> = {
  category: "Направление",
  issue: "Проблема",
  where: "Где",
  device: "Устройство",
  method: "Способ оплаты",
  when: "Когда (со слов юзера)",
  description: "Описание",
};

interface OperatorLite {
  id: number;
  telegram_id: number;
  display_name: string | null;
  role: string;
  is_active: boolean;
}

/** Меню действий строки. Держим в поповере, чтобы таблица не разъезжалась
 *  от пяти кнопок в каждой строке. */
function RowMenu({
  ticket,
  operators,
  isAdmin,
  onOpen,
  onAssign,
  onReopen,
  onClose,
}: {
  ticket: Ticket;
  operators: OperatorLite[];
  isAdmin: boolean;
  onOpen: () => void;
  onAssign: (agentId: number | null) => void;
  onReopen: () => void;
  onClose: () => void;
}) {
  const [assigning, setAssigning] = useState(false);
  const navigate = useNavigate();
  const closed = ticket.status === "closed";

  const item = (close: () => void, label: string, fn: () => void, tone?: "danger") => (
    <button
      onClick={() => {
        close();
        setAssigning(false);
        fn();
      }}
      className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-colors hover:bg-white/[0.07] ${
        tone === "danger" ? "text-rose-300 hover:bg-rose-500/10" : "text-zinc-300"
      }`}
    >
      {label}
    </button>
  );

  return (
    <RowActions>
      {(close) => (
        <>
          {item(close, "Открыть переписку", onOpen)}
          {item(close, "Карточка пользователя", () => navigate(`/users/${ticket.user_id}`))}
          {ticket.user_telegram_id != null &&
            item(close, "Скопировать telegram_id", () => {
              navigator.clipboard.writeText(String(ticket.user_telegram_id));
              popup({
                tone: "success",
                title: "Скопировано",
                body: String(ticket.user_telegram_id),
              });
            })}
          {ticket.username &&
            item(close, "Написать в Telegram", () =>
              window.open(`https://t.me/${ticket.username}`, "_blank"),
            )}

          {isAdmin && (
            <>
              <div className="my-1 mx-2 border-t border-white/[0.07]" />
              {!assigning ? (
                <button
                  onClick={() => setAssigning(true)}
                  className="w-full text-left px-3 py-2 rounded-lg text-[13px] text-zinc-300 transition-colors hover:bg-white/[0.07]"
                >
                  Передать оператору…
                </button>
              ) : (
                <div className="px-2 py-1.5 space-y-1">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-600 px-1">
                    Передать
                  </div>
                  {operators
                    .filter((o) => o.is_active && o.id !== ticket.agent_id)
                    .map((o) => (
                      <button
                        key={o.id}
                        onClick={() => {
                          close();
                          setAssigning(false);
                          onAssign(o.id);
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-[13px] text-zinc-300 hover:bg-white/[0.07]"
                      >
                        {o.display_name || o.telegram_id}
                      </button>
                    ))}
                  {ticket.agent_id != null &&
                    item(close, "Снять назначение", () => onAssign(null))}
                </div>
              )}
            </>
          )}

          <div className="my-1 mx-2 border-t border-white/[0.07]" />
          {closed
            ? item(close, "Вернуть в работу", onReopen)
            : item(close, "Закрыть обращение", onClose, "danger")}
        </>
      )}
    </RowActions>
  );
}

export default function Tickets() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>("");
  const [openId, setOpenId] = useState<number | null>(null);
  const [reply, setReply] = useState("");

  const q = useQuery<{ items: Ticket[]; total: number }>({
    queryKey: ["tickets", filter],
    queryFn: () => api(`/api/admin/tickets${filter ? `?status_filter=${filter}` : ""}`),
    refetchInterval: 30000,
  });

  const detail = useQuery<TicketDetail>({
    queryKey: ["ticket", openId],
    queryFn: () => api(`/api/admin/tickets/${openId}`),
    enabled: openId !== null,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["tickets"] });
    if (openId) qc.invalidateQueries({ queryKey: ["ticket", openId] });
  };

  const send = useMutation({
    mutationFn: () =>
      api(`/api/admin/tickets/${openId}/reply`, {
        method: "POST",
        body: JSON.stringify({ text: reply }),
      }),
    onSuccess: () => {
      popup({ tone: "success", title: "Отправлено", body: "Ответ ушёл пользователю." });
      setReply("");
      refresh();
    },
    onError: (e) => popup({ tone: "error", title: "Ответ", body: (e as Error).message }),
  });

  const close = useMutation({
    mutationFn: () => api(`/api/admin/tickets/${openId}/close`, { method: "POST" }),
    onSuccess: () => {
      popup({ tone: "success", title: "Закрыто", body: "Обращение закрыто." });
      refresh();
    },
    onError: (e) => popup({ tone: "error", title: "Закрытие", body: (e as Error).message }),
  });

  // Кто я — от роли зависит доступность «Передать» (ТЗ-03 §2).
  const me = useQuery<{ telegram_id: number; role: string }>({
    queryKey: ["admin-me"],
    queryFn: () => api("/api/admin/me"),
    staleTime: 300000,
  });
  const operators = useQuery<OperatorLite[]>({
    queryKey: ["operators"],
    queryFn: () => api("/api/admin/operators"),
    enabled: me.data?.role === "admin",
    staleTime: 60000,
  });

  const closeTicket = useMutation({
    mutationFn: (id: number) => api(`/api/admin/tickets/${id}/close`, { method: "POST" }),
    onSuccess: () => {
      popup({ tone: "success", title: "Закрыто", body: "Обращение закрыто." });
      refresh();
    },
    onError: (e) => popup({ tone: "error", title: "Закрытие", body: (e as Error).message }),
  });

  const reopen = useMutation({
    mutationFn: (id: number) => api(`/api/admin/tickets/${id}/reopen`, { method: "POST" }),
    onSuccess: () => {
      popup({ tone: "success", title: "В работе", body: "Обращение снова открыто." });
      refresh();
    },
    onError: (e) => popup({ tone: "error", title: "Переоткрытие", body: (e as Error).message }),
  });

  const assign = useMutation({
    mutationFn: (v: { id: number; agentId: number | null }) =>
      api(`/api/admin/tickets/${v.id}/assign`, {
        method: "POST",
        body: JSON.stringify({ agent_id: v.agentId }),
      }),
    onSuccess: () => {
      popup({ tone: "success", title: "Передано", body: "Оператор уведомлён." });
      refresh();
    },
    onError: (e) => popup({ tone: "error", title: "Передача", body: (e as Error).message }),
  });

  const filters: [string, string][] = [
    ["", "Все"],
    ["new", "Новые"],
    ["in_progress", "В работе"],
    ["waiting_user", "Ждут юзера"],
    ["closed", "Закрытые"],
  ];

  return (
    <div className="p-4 md:p-8 space-y-4">
      <PageHeader title="Обращения" />

      <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]">
        {filters.map(([v, label]) => (
          <button
            key={v || "all"}
            onClick={() => setFilter(v)}
            className={`h-7 px-3 rounded-md text-xs font-medium transition-all ${
              filter === v ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <Table>
        <thead>
          <tr>
            <Th fit>#</Th>
            <Th>Пользователь</Th>
            <Th>Тема</Th>
            <Th fit>Статус</Th>
            <Th>Оператор</Th>
            <Th fit>Оценка</Th>
            <Th fit>Создано</Th>
            <Th fit align="right">Действия</Th>
          </tr>
        </thead>
        <tbody>
          {(q.data?.items ?? []).map((tk) => {
            const st = STATUS[tk.status] ?? { label: tk.status, tone: "neutral" as const };
            const topic = [tk.form?.category, tk.form?.issue].filter(Boolean).join(" · ");
            return (
              <tr
                key={tk.id}
                onClick={() => setOpenId(tk.id)}
                className="border-t border-white/[0.04] hover:bg-white/[0.03] cursor-pointer transition-colors"
              >
                <Td fit>
                  <span className="font-semibold text-zinc-100">#{tk.id}</span>
                </Td>
                <Td>
                  <div className="text-zinc-200">{tk.username ? `@${tk.username}` : "—"}</div>
                  <div className="text-[11px] text-zinc-500 tabular-nums">{tk.user_telegram_id}</div>
                </Td>
                <Td>
                  <div className="max-w-[320px] truncate text-zinc-300">
                    {tk.form?.description || "— без описания —"}
                  </div>
                  {topic && <div className="text-[11px] text-zinc-500 truncate">{topic}</div>}
                </Td>
                <Td fit>
                  <Pill tone={st.tone}>{st.label}</Pill>
                </Td>
                <Td className="text-zinc-400">{tk.agent_name || <span className="text-zinc-600">не взято</span>}</Td>
                <Td fit>{stars(tk.rating)}</Td>
                <Td fit className="text-zinc-500 text-[11px] tabular-nums whitespace-nowrap">
                  {tk.created_at ? fmtDate(tk.created_at) : "—"}
                </Td>
                <Td fit align="right">
                  <RowMenu
                    ticket={tk}
                    operators={operators.data ?? []}
                    isAdmin={me.data?.role === "admin"}
                    onOpen={() => setOpenId(tk.id)}
                    onAssign={(agentId) => assign.mutate({ id: tk.id, agentId })}
                    onReopen={() => reopen.mutate(tk.id)}
                    onClose={() => closeTicket.mutate(tk.id)}
                  />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {q.data && q.data.items.length === 0 && (
        <p className="text-sm text-zinc-500">Обращений нет.</p>
      )}

      <Modal open={openId !== null} onClose={() => setOpenId(null)} title={`Обращение #${openId ?? ""}`}>
        {detail.isLoading && <p className="text-sm text-zinc-500">Загрузка…</p>}
        {detail.data && (
          <div className="space-y-4">
            <div className="rounded-lg bg-black/20 border border-white/[0.06] p-3 space-y-1">
              {Object.entries(detail.data.form ?? {})
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k} className="text-sm">
                    <span className="text-zinc-500">{FIELDS[k] ?? k}: </span>
                    <span className="text-zinc-200">{String(v)}</span>
                  </div>
                ))}
            </div>

            <div className="space-y-2 max-h-72 overflow-y-auto">
              {detail.data.messages.map((m) => (
                <div
                  key={m.id}
                  className={`text-sm rounded-lg p-2.5 ${
                    m.author === "user"
                      ? "bg-white/[0.04] border border-white/[0.06]"
                      : "bg-brand-soft/40 border border-brand/20"
                  }`}
                >
                  <div className="text-[11px] text-zinc-500 mb-0.5">
                    {m.author === "user" ? "Пользователь" : "Поддержка"} ·{" "}
                    {m.created_at ? fmtDate(m.created_at) : ""}
                  </div>
                  <div className="text-zinc-200 whitespace-pre-wrap">
                    {m.text || (m.has_attachment ? "📎 вложение" : "")}
                  </div>
                </div>
              ))}
            </div>

            {detail.data.status !== "closed" && (
              <div className="space-y-2">
                <Input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Ответ пользователю…"
                />
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    disabled={!reply.trim() || send.isPending}
                    onClick={() => send.mutate()}
                  >
                    {send.isPending ? "Отправляю…" : "Ответить"}
                  </Button>
                  <Button variant="danger" disabled={close.isPending} onClick={() => close.mutate()}>
                    Закрыть обращение
                  </Button>
                </div>
                <p className="text-xs text-zinc-600">
                  Ответ уходит пользователю в бот от лица сервиса — имя оператора не раскрывается.
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
